import React from 'react';
import {
  Box,
  Typography,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Paper,
  Chip,
  Tooltip,
  Divider,
} from '@mui/material';

// Regulatory Compliance - Risk Detection Categories
const COMPLIANCE_RISK_CATEGORIES = [
  { code: 'harm', name: 'Harmful Content', description: 'Content considered generally harmful' },
  { code: 'social_bias', name: 'Social Bias', description: 'Prejudice based on identity or characteristics' },
  { code: 'jailbreak', name: 'Jailbreak Attempt', description: 'Attempts to manipulate AI to generate harmful content' },
  { code: 'violence', name: 'Violence', description: 'Content promoting physical, mental, or sexual harm' },
  { code: 'profanity', name: 'Profanity', description: 'Use of offensive language or insults' },
  { code: 'sexual_content', name: 'Sexual Content', description: 'Explicit or suggestive material of a sexual nature' },
  { code: 'unethical_behavior', name: 'Unethical Behavior', description: 'Actions that violate moral or legal standards' },
];

// Regulatory Compliance - RAG Quality Assessment Categories
const COMPLIANCE_RAG_CATEGORIES = [
  { code: 'relevance', name: 'Context Relevance', description: 'Check if retrieved context is relevant to the query' },
  { code: 'groundedness', name: 'Response Groundedness', description: 'Check if response is accurate and faithful to context' },
  { code: 'answer_relevance', name: 'Answer Relevance', description: 'Check if response directly addresses the user query' },
];

interface GraniteCategorySelectorProps {
  value: string[];
  onChange: (selected: string[]) => void;
  disabled?: boolean;
}

export const GraniteCategorySelector: React.FC<GraniteCategorySelectorProps> = ({
  value = [],
  onChange,
  disabled = false
}) => {
  const handleToggle = (code: string) => {
    if (value.includes(code)) {
      onChange(value.filter(c => c !== code));
    } else {
      onChange([...value, code]);
    }
  };

  const totalCategories = COMPLIANCE_RISK_CATEGORIES.length + COMPLIANCE_RAG_CATEGORIES.length;

  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom>
        Ignored Regulatory Compliance Categories
        <Tooltip title="Select categories that should be ignored during regulatory compliance analysis. This allows you to skip checks that aren't relevant to your API.">
          <Chip 
            label="?" 
            size="small" 
            sx={{ ml: 1, height: 20, fontSize: '0.7rem', cursor: 'help' }}
            tabIndex={0}
          />
        </Tooltip>
      </Typography>
      
      {value.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" color="text.secondary">
            {value.length} {value.length === 1 ? 'category' : 'categories'} will be ignored during analysis
          </Typography>
        </Box>
      )}

      <Paper variant="outlined" sx={{ p: 2, maxHeight: 500, overflow: 'auto' }}>
        {/* Risk Detection Categories */}
        <Typography variant="subtitle2" sx={{ mb: 1, color: 'primary.main', fontWeight: 'bold' }}>
          Risk Detection Categories ({COMPLIANCE_RISK_CATEGORIES.length})
        </Typography>
        <FormGroup>
          {COMPLIANCE_RISK_CATEGORIES.map((category) => (
            <Tooltip key={category.code} title={category.description} placement="right">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={value.includes(category.code)}
                    onChange={() => handleToggle(category.code)}
                    disabled={disabled}
                    size="small"
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2">
                      <strong>{category.name}</strong>
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {category.description}
                    </Typography>
                  </Box>
                }
                sx={{ mb: 1 }}
              />
            </Tooltip>
          ))}
        </FormGroup>

        <Divider sx={{ my: 2 }} />

        {/* RAG Assessment Categories */}
        <Typography variant="subtitle2" sx={{ mb: 1, color: 'secondary.main', fontWeight: 'bold' }}>
          RAG Quality Assessment ({COMPLIANCE_RAG_CATEGORIES.length})
        </Typography>
        <FormGroup>
          {COMPLIANCE_RAG_CATEGORIES.map((category) => (
            <Tooltip key={category.code} title={category.description} placement="right">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={value.includes(category.code)}
                    onChange={() => handleToggle(category.code)}
                    disabled={disabled}
                    size="small"
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2">
                      <strong>{category.name}</strong>
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {category.description}
                    </Typography>
                  </Box>
                }
                sx={{ mb: 1 }}
              />
            </Tooltip>
          ))}
        </FormGroup>
      </Paper>

      {value.length === totalCategories && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="caption" color="warning.main">
            Warning: All categories are ignored. No regulatory compliance analysis will be performed.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default GraniteCategorySelector;







